import { useTheme } from "@emotion/react";
import {
    Box, Card, ListItem, ListItemIcon, ListItemText, Typography, Collapse
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { ClockIcon } from "src/assets/icons/ClockIcon";
import { useLocales } from "src/locales";
import { checkOpeningHours, getNextOpenDay, getOpensClosesHour } from "src/utils/dateUtils";
import Iconify from "./iconify";
import useResponsive from "src/hooks/useResponsive";

const WEEK_ORDER = [
    'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
];

function OpeningHours({ hours, top = 44 }) {
    const { translate } = useLocales();
    const theme = useTheme();
    const [hover, setHover] = useState(false);
    const isMobile = useResponsive("down", "sm");
    const [openOpeningHours, setOpenOpeningHours] = useState(false);

    const isOpen = checkOpeningHours(hours);
    const displayedHour = getOpensClosesHour(hours, isOpen);
    const { name: nextDayName, openAt: nextDayHour } = getNextOpenDay(hours);
    const displayedHourText = displayedHour
        ? isOpen
            ? `${translate("productPage.closesAt")} ${displayedHour}`
            : `${translate("productPage.opensAt")} ${displayedHour}`
        : `${translate("productPage.opens")} ` + translate(
            `daysOfTheWeekShort.${nextDayName.charAt(0).toLowerCase() + nextDayName.slice(1)}`
        ) + `, ${nextDayHour}`;


    const normalizedOpeningHours = useMemo(() => {
        if (!hours?.length) return [];
        const byName = new Map(
            hours.map(h => [h.name, h])
        );

        return WEEK_ORDER.map(name => {
            const item = byName.get(name);
            if (!item) return { name, openAt: null, closeAt: null, closed: true };

            return { ...item, name, closed: false };
        })
    }, [hours])

    const toggleOpeningHours = () => {
        if (isMobile) setOpenOpeningHours((p) => !p);
    };

    useEffect(() => {
        if (!isMobile) setOpenOpeningHours(false);
    }, [isMobile]);

    return (
        <Box
            sx={{ position: "relative", display: "inline-block", width: "auto", zIndex: 1 }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
        >
            <ListItem
                sx={{
                    px: 0,
                    cursor: "pointer",
                    userSelect: "none",
                    WebkitTapHighlightColor: "transparent",
                    "&:focus": { outline: "none" },
                    "& *": { WebkitTapHighlightColor: "transparent" },
                }} onClick={toggleOpeningHours}
            >
                <ListItemIcon
                    sx={{
                        color: isOpen ? theme.palette.success.main : theme.palette.error.main,
                    }}
                >
                    <ClockIcon color={theme.palette.text.primary}/>
                </ListItemIcon>

                <ListItemText
                    primaryTypographyProps={{
                        variant: "subtitle1",
                        textAlign: "center",
                        sx: { color: isOpen ? theme.palette.success.main : theme.palette.error.main },
                    }}
                    primary={isOpen ? translate("productPage.openNow") : translate("productPage.closedNow")}
                />

                <ListItemText primary="·" primaryTypographyProps={{ variant: "subtitle1", px: 1 }} />

                <ListItemText primary={displayedHourText} primaryTypographyProps={{ variant: "subtitle1", textAlign: "center" }} />

                <ListItemIcon sx={{ mr: 0 }}>
                    <Iconify
                        icon="eva:chevron-down-fill"
                        width={24}
                        sx={{
                            display: "inline-flex",
                            transition: (t) =>
                                t.transitions.create("transform", {
                                    duration: 300,
                                    easing: t.transitions.easing.easeInOut,
                                }),
                            transform: isMobile ? openOpeningHours ? "rotate(180deg)" : "rotate(0deg)" : hover ? "rotate(180deg)" : "rotate(0deg)",
                            willChange: "transform",
                        }}
                        aria-hidden
                    />
                </ListItemIcon>
            </ListItem>

            {isMobile && (
                <Collapse
                    in={openOpeningHours}
                    timeout={300}
                    collapsedSize={0}
                    unmountOnExit={false}
                >
                    <Box
                        sx={{
                            display: "flex",
                            pl: 5,
                            gap: 1,
                            marginY: 2,
                            opacity: openOpeningHours ? 1 : 0,
                            transition: (t) =>
                                t.transitions.create("opacity", {
                                    duration: 250,
                                    easing: t.transitions.easing.easeInOut,
                                }),
                            pointerEvents: openOpeningHours ? "auto" : "none",
                        }}
                        role="region"
                        aria-live="polite"
                    >
                        <Box sx={{ display: "flex", flexDirection: "column", flex: 0.9, gap: 1 }}>
                            {normalizedOpeningHours.map((hour) => (
                                <Typography
                                    key={hour.name}
                                    variant="body1"
                                    sx={{ textTransform: "capitalize" }}
                                >
                                    {translate(
                                        `daysOfTheWeek.${hour.name.charAt(0).toLowerCase() + hour.name.slice(1)}`
                                    )}
                                </Typography>
                            ))}
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, gap: 1 }}>
                            {normalizedOpeningHours.map((hour, i) => (
                                <Typography key={`${hour.name}-${i}`} variant="body1">
                                    {hour.closed ? translate('productPage.closedNow') : `${hour.openAt} - ${hour.closeAt}`}
                                </Typography>
                            ))}
                        </Box>
                    </Box>
                </Collapse>
            )}

            {!isMobile && hover && (
                <Card
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        py: 2,
                        px: 4,
                        gap: 1,
                        width: "100%",
                        minWidth: 280,
                        position: "absolute",
                        top: top,
                        left: 0,
                    }}
                >
                    {normalizedOpeningHours.map((hour) => (
                        <Box key={hour.name} sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography variant="body1" sx={{ textTransform: "capitalize" }}>
                                {translate(
                                    `daysOfTheWeek.${hour.name.charAt(0).toLowerCase() + hour.name.slice(1)}`
                                )}
                            </Typography>
                            <Typography variant="body1">{hour.closed ? translate('productPage.closedNow') : `${hour.openAt} - ${hour.closeAt}`}</Typography>
                        </Box>
                    ))}
                </Card>
            )}
        </Box>
    );
}

export default OpeningHours;
